import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaBell,
  FaBriefcase,
  FaComments,
  FaDollarSign,
  FaClock,
  FaCubes,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import {
  clearAllChatNotifications,
  getCombinedNotifications,
} from '../../utils/chatNotifications';
import { graphqlRequest } from '../../utils/graphqlRequest';
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  getAnnouncementPreferenceKey,
  readNotificationSettings,
  writeNotificationSettings,
  getAdminNotificationDestination,
  buildAdminNotificationUrl,
} from '../../utils/notificationPreferences';
import '../../styles/AdminNotificationSettings.css';

const BASE_SECTIONS = [
  {
    key: 'job-alerts',
    title: 'Job Alerts',
    icon: <FaBriefcase />,
    items: [
      {
        key: 'job-cancelled',
        label: 'Job Cancelled',
        description: 'Alert when a scheduled job is cancelled',
        enabled: true,
      },
      {
        key: 'schedule-changed',
        label: 'Schedule Changed',
        description: 'Notify when job time or location changes',
        enabled: true,
      },
      {
        key: 'urgent-project',
        label: 'Urgent Project',
        description:
          'When admin marks a project task as urgent or high priority',
        enabled: true,
        managerOnly: true,
      },
    ],
  },
  {
    key: 'resources',
    title: 'Resources',
    icon: <FaCubes />,
    items: [
      {
        key: 'low-stock-alert',
        label: 'Low stock alert',
        description: 'When inventory drops to a low level or runs out',
        enabled: true,
      },
      {
        key: 'goods-damaged',
        label: 'Goods damaged',
        description: 'When damaged or broken goods are reported on inventory',
        enabled: true,
      },
      {
        key: 'product-expiry-alert',
        label: 'Product expiry alert',
        description: 'When products are within 30 days of their expiry date',
        enabled: true,
      },
    ],
  },
  {
    key: 'operational',
    title: 'Operational',
    icon: <FaClock />,
    items: [
      {
        key: 'clock-in-reminder',
        label: 'Clock-in Reminder',
        description: 'In-app reminder each time you sign in to the admin portal',
        enabled: true,
      },
      {
        key: 'shift-reminder',
        label: 'Shift Reminder',
        description: 'Alert 30 minutes before shift starts',
        enabled: true,
      },
      {
        key: 'timesheet-submission-reminder',
        label: 'Timesheet Submission Reminder',
        description: 'Weekly reminder to submit timesheet',
        enabled: false,
      },
      {
        key: 'report-submission-reminder',
        label: 'Report Submission Reminder',
        description: 'Remind to submit work reports',
        enabled: true,
      },
      {
        key: 'leave-reminder',
        label: 'Leave reminder',
        description:
          'Managers: when staff apply leave. Staff: when leave is approved or declined',
        enabled: true,
      },
      {
        key: 'work-report-submission',
        label: 'Work report submission',
        description:
          'When a contractor or cleaner submits a new work report for review',
        enabled: true,
        managerOnly: true,
      },
      {
        key: 'pipeline-files',
        label: 'Pipeline Files updates',
        description:
          'When a new file is uploaded to a lead or a work report is approved (Pipeline → Files)',
        enabled: true,
      },
      {
        key: 'new-team-member',
        label: 'New team member',
        description:
          'When a new team member is added through Work Force → Team Member',
        enabled: true,
      },
      {
        key: 'workforce-document-added',
        label: 'Workforce document',
        description:
          'When a document is uploaded under Work Force → Document',
        enabled: true,
      },
      {
        key: 'document-expiry-alert',
        label: 'Document / visa expiry (email + in-app)',
        description:
          'Send expiry reminders to employees whose documents or visas are due soon — email and in-app notification (daily + on-demand from Documents)',
        enabled: true,
      },
    ],
  },
  {
    key: 'financial',
    title: 'Financial',
    icon: <FaDollarSign />,
    items: [
      {
        key: 'payment-received',
        label: 'Payment Received',
        description: 'Notify when payment is deposited',
        enabled: true,
      },
      {
        key: 'invoice-generated',
        label: 'Invoice or Payslip Generated',
        description: 'Alert when new invoice or payslip is created',
        enabled: true,
      },
      {
        key: 'quotation-accepted',
        label: 'Quotation Accepted',
        description:
          'When a client accepts a quotation from the client portal',
        enabled: true,
      },
      {
        key: 'quotation-rejected',
        label: 'Quotation Rejected',
        description:
          'When a client rejects a quotation from the client portal',
        enabled: true,
      },
      {
        key: 'contract-accepted',
        label: 'Contract Signed',
        description:
          'When a client signs a contract from the review link',
        enabled: true,
      },
      {
        key: 'contract-rejected',
        label: 'Contract Change or Decline',
        description:
          'When a client requests changes or declines a contract',
        enabled: true,
      },
      {
        key: 'proposal-accepted',
        label: 'Proposal Accepted',
        description:
          'When a client accepts a proposal from the review link',
        enabled: true,
      },
      {
        key: 'proposal-rejected',
        label: 'Proposal Change or Decline',
        description:
          'When a client requests changes or declines a proposal',
        enabled: true,
      },
      {
        key: 'payment-pending',
        label: 'Payment Pending',
        description: 'Remind about pending payments',
        enabled: true,
      },
    ],
  },
  {
    key: 'communication',
    title: 'Communication',
    icon: <FaComments />,
    items: [
      {
        key: 'new-chat-message',
        label: 'New Chat Message',
        description: 'Alert for all new messages',
        enabled: true,
      },
      {
        key: 'manager-mention',
        label: 'Manager Mention',
        description: 'When manager mentions you',
        enabled: true,
      },
      {
        key: 'support-message',
        label: 'Support Message',
        description: 'Updates from support team',
        enabled: false,
      },
      {
        key: 'notes-communication',
        label: 'Notes communication',
        description:
          'When a teammate adds an internal note on a reported issue (Support Center)',
        enabled: true,
      },
    ],
  },
];

const PORTAL_CONFIG = {
  manager: {
    subtitle: 'Manage your manager notification preferences',
    hiddenSectionKeys: [],
  },
  contractor: {
    subtitle: 'Manage your contractor notification preferences',
    hiddenSectionKeys: [],
  },
  cleaner: {
    subtitle: 'Manage your cleaner notification preferences',
    hiddenSectionKeys: [],
  },
};

function NotificationToggle({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      className={`notif-toggle ${checked ? 'on' : ''}`}
      onClick={onChange}
      disabled={disabled}
      aria-pressed={checked}
      aria-label={`Toggle ${label}`}
    >
      <span className="notif-toggle-knob" />
    </button>
  );
}

function readAdminUserId() {
  const fromCookie = Cookies.get('userId');
  if (fromCookie) return fromCookie;
  try {
    const token = Cookies.get('accessToken');
    if (!token) return '';
    return jwtDecode(token)?.sub?.userId || '';
  } catch {
    return '';
  }
}

const GET_BACKEND_NOTIFICATIONS = `
  query GetNotifications($userId: String) {
    getNotifications(userId: $userId) {
      id
      userId
      title
      message
      createdAt
    }
  }
`;

const MY_ADMIN_NOTIFICATION_PREFS_QUERY = `
  query MyAdminNotificationPreferences {
    myAdminNotificationPreferences
  }
`;

const UPDATE_MY_ADMIN_NOTIFICATION_PREFS = `
  mutation UpdateMyAdminNotificationPreferences($patch: JSON!) {
    updateMyAdminNotificationPreferences(patch: $patch)
  }
`;

function parseProfileNoteMeta(message) {
  const raw = String(message || '');
  const m = raw.match(/\[\[profileNote:([^:\]]+):([^:\]]+)\]\]/);
  if (!m) return { cleanMessage: raw, userId: '', noteId: '' };
  const cleanMessage = raw.replace(m[0], '').trim();
  return { cleanMessage, userId: m[1] || '', noteId: m[2] || '' };
}

const backendSeenKey = (userId) => `admin-backend-notif-last-seen:${userId || 'unknown'}`;

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(() => readAdminUserId());
  const user = useMemo(() => ({ id: userId, portal: 'manager' }), [userId]);
  const currentPortal = 'manager';

  useEffect(() => {
    setUserId(readAdminUserId());
  }, []);
  const portalConfig = PORTAL_CONFIG[currentPortal] || PORTAL_CONFIG.cleaner;
  const [recentNotifications, setRecentNotifications] = useState(() =>
    getCombinedNotifications(),
  );

  const visibleSections = useMemo(
    () =>
      BASE_SECTIONS.filter(
        (section) => !portalConfig.hiddenSectionKeys.includes(section.key),
      ).map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => !item.managerOnly || currentPortal === 'manager',
        ),
      })),
    [portalConfig.hiddenSectionKeys, currentPortal],
  );

  const defaultSettings = useMemo(
    () =>
      visibleSections.reduce((acc, section) => {
        section.items.forEach((item) => {
          acc[item.key] = item.enabled;
        });
        return acc;
      }, {}),
    [visibleSections],
  );

  const [settings, setSettings] = useState(() =>
    user?.id
      ? readNotificationSettings(user.id, currentPortal, defaultSettings)
      : { ...DEFAULT_NOTIFICATION_SETTINGS, ...defaultSettings },
  );
  const [prefsLoading, setPrefsLoading] = useState(true);

  // Hydrate toggles from server (authoritative); fall back to localStorage if the query fails.
  useEffect(() => {
    if (!user?.id) {
      setPrefsLoading(false);
      return;
    }
    let cancelled = false;
    setPrefsLoading(true);
    (async () => {
      try {
        const data = await graphqlRequest(MY_ADMIN_NOTIFICATION_PREFS_QUERY);
        const server = data?.myAdminNotificationPreferences;
        if (cancelled) return;
        if (server && typeof server === 'object') {
          const merged = {
            ...DEFAULT_NOTIFICATION_SETTINGS,
            ...defaultSettings,
            ...server,
          };
          setSettings(merged);
          writeNotificationSettings(user.id, currentPortal, merged);
        } else {
          setSettings(readNotificationSettings(user.id, currentPortal, defaultSettings));
        }
      } catch (e) {
        console.error('Failed to load admin notification preferences:', e);
        if (!cancelled) {
          setSettings(readNotificationSettings(user.id, currentPortal, defaultSettings));
        }
      } finally {
        if (!cancelled) setPrefsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, currentPortal, defaultSettings]);

  const filteredRecentNotifications = useMemo(() => {
    const filtered = recentNotifications.filter((item) => {
      if (item?.type === 'announcement') {
        const prefKey = getAnnouncementPreferenceKey({
          title: item?.title,
          message: item?.content,
        });
        if (!prefKey) return true;
        return Boolean(settings[prefKey]);
      }
      if (item?.type === 'backend') {
        return true;
      }
      const isMention =
        typeof item?.content === 'string' && item.content.trimStart().startsWith('@You');
      if (isMention) {
        return Boolean(settings['manager-mention']);
      }
      return Boolean(settings['new-chat-message']);
    });
    const timeMs = (item) => {
      const t = item?.createdAt ? new Date(item.createdAt).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };
    const isInventoryReminder = (item) =>
      /synthetic-(lowstock|expiry)-/i.test(String(item?.id || ''));
    return [...filtered].sort((a, b) => {
      const synA = isInventoryReminder(a);
      const synB = isInventoryReminder(b);
      if (synA !== synB) return synA ? 1 : -1;
      return timeMs(b) - timeMs(a);
    });
  }, [recentNotifications, settings]);

  const handleToggle = async (itemKey) => {
    if (prefsLoading || !user?.id) return;
    const nextVal = !settings[itemKey];
    setSettings((prev) => ({
      ...prev,
      [itemKey]: nextVal,
    }));
    try {
      const data = await graphqlRequest(UPDATE_MY_ADMIN_NOTIFICATION_PREFS, {
        patch: { [itemKey]: nextVal },
      });
      const merged = data?.updateMyAdminNotificationPreferences;
      if (merged && typeof merged === 'object') {
        setSettings((prev) => ({
          ...DEFAULT_NOTIFICATION_SETTINGS,
          ...prev,
          ...merged,
        }));
        writeNotificationSettings(user.id, currentPortal, {
          ...DEFAULT_NOTIFICATION_SETTINGS,
          ...merged,
        });
      }
    } catch (e) {
      console.error('Failed to save notification preference:', e);
      setSettings((prev) => ({
        ...prev,
        [itemKey]: !nextVal,
      }));
      toast.error('Could not save notification preference. Please try again.');
    }
  };

  useEffect(() => {
    const syncNotifications = () => {
      setRecentNotifications(getCombinedNotifications());
    };

    syncNotifications();
    window.addEventListener('admin-chat-unread-updated', syncNotifications);
    return () =>
      window.removeEventListener('admin-chat-unread-updated', syncNotifications);
  }, []);

  // Load backend notifications (profile notes, system alerts).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await graphqlRequest(GET_BACKEND_NOTIFICATIONS, { userId });
        const items = Array.isArray(data?.getNotifications) ? data.getNotifications : [];
        const normalized = items.map((n) => {
          const { cleanMessage, userId: targetUserId, noteId } = parseProfileNoteMeta(n?.message);
          return {
            id: `backend-${n.id}`,
            type: 'backend',
            title: n?.title || 'Notification',
            content: cleanMessage || '',
            createdAt: n?.createdAt,
            meta: { targetUserId, noteId },
          };
        });
        if (!cancelled) {
          setRecentNotifications((prev) => {
            const localChat = (Array.isArray(prev) ? prev : []).filter(
              (item) => item?.type !== 'backend',
            );
            const merged = [...normalized, ...localChat];
            const seen = new Set();
            return merged.filter((x) => {
              if (!x?.id) return false;
              if (seen.has(x.id)) return false;
              seen.add(x.id);
              return true;
            });
          });
        }
      } catch {
        // ignore (page still works for chat/announcement notifications)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Mark backend notifications as "seen" when opening this page.
  useEffect(() => {
    if (!userId) return;
    localStorage.setItem(backendSeenKey(userId), new Date().toISOString());
    window.dispatchEvent(new Event('admin-backend-notifications-seen'));
  }, [userId]);

  const formatDateTime = (value) => {
    const date = value ? new Date(value) : new Date();
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString([], {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const openNotificationDestination = (item) => {
    if (item?.type === 'backend') {
      const meta = item?.meta || {};
      if (meta?.targetUserId && meta?.noteId) {
        navigate(`/profile-dashboard/${meta.targetUserId}?tab=notes&noteId=${meta.noteId}`);
        return;
      }
      const dest = getAdminNotificationDestination({
        type: 'announcement',
        title: item?.title,
        content: item?.content,
      });
      navigate(buildAdminNotificationUrl(dest));
      return;
    }
    const dest = getAdminNotificationDestination(item);
    navigate(buildAdminNotificationUrl(dest));
  };

  return (
    <div className="notif-page">
      <header className="notif-header">
        <button
          type="button"
          className="notif-back-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FaArrowLeft />
        </button>
        <div className="notif-header-content">
          <h1>Notification Settings</h1>
          <p>{portalConfig.subtitle}</p>
        </div>
      </header>

      <main className="notif-content">
        <section className="notif-card">
          <div className="notif-card-title">
            <span className="notif-card-icon">
              <FaBell />
            </span>
            <h2>Recent Notifications</h2>
          </div>
          <div className="notif-feed-actions">
            <button
              type="button"
              className="notif-feed-btn"
              onClick={() => navigate('/?page=chat')}
            >
              Open chat
            </button>
            <button
              type="button"
              className="notif-feed-btn notif-feed-btn-clear"
              onClick={() => clearAllChatNotifications()}
            >
              Clear all
            </button>
          </div>
          <div className="notif-feed-list">
            {filteredRecentNotifications.length === 0 ? (
              <p className="notif-feed-empty">No notifications yet.</p>
            ) : (
              filteredRecentNotifications.slice(0, 30).map((item) => (
                <div key={item.id} className="notif-feed-item">
                  <p className="notif-feed-title">
                    {item.type === 'announcement'
                      ? item.title || 'Announcement'
                      : item.type === 'backend'
                        ? item.title || 'Notification'
                        : item.senderName || 'New message'}
                  </p>
                  <p className="notif-feed-message">
                    {item.type === 'announcement'
                      ? item.content || ''
                      : item.type === 'backend'
                        ? item.content || ''
                        : item.content || (item.hasAttachment ? 'Sent an attachment' : 'New message')}
                  </p>
                  <small className="notif-feed-time">{formatDateTime(item.createdAt)}</small>
                  <div className="notif-feed-actions-row">
                    <button
                      type="button"
                      className="notif-feed-btn"
                      onClick={() => openNotificationDestination(item)}
                    >
                      Open
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        {visibleSections.map((section) => (
          <section key={section.key} className="notif-card">
            <div className="notif-card-title">
              <span className="notif-card-icon">{section.icon}</span>
              <h2>{section.title}</h2>
            </div>

            <div className="notif-list">
              {section.items.map((item) => (
                <div key={item.key} className="notif-item">
                  <div className="notif-item-text">
                    <p className="notif-item-label">{item.label}</p>
                    <p className="notif-item-description">{item.description}</p>
                  </div>
                  <NotificationToggle
                    checked={Boolean(settings[item.key])}
                    onChange={() => {
                      void handleToggle(item.key);
                    }}
                    label={item.label}
                    disabled={prefsLoading}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>

      <div className="notif-floating-bell" aria-hidden>
        <FaBell />
      </div>
    </div>
  );
}
